import React, { useState, useEffect, useMemo } from "react";
import useUpdateProfile from "../../lib/useUpdateProfile";
import { HiOutlineXMark } from "react-icons/hi2";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "../../../Context/AuthContext";
import { useReadContactPerson, useUpdateContactPerson } from "../../lib/useReadUpdateContactPerson";
import { useQueryClient } from "react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Updatecusterprofile.css";

const UpdatecustomerProfile = () => {
  const { mutation } = useUpdateProfile();
  const { data: contactPersonData, isLoading: contactLoading } = useReadContactPerson();
  const { mutate: updateContactPerson, isLoading: updatingContact } = useUpdateContactPerson();
  const { profileData, profileLoading } = useContext(AuthContext);
  const [activeSection, setActiveSection] = useState("UpdatecustomerProfile");
  const queryClient = useQueryClient();
  const activeStyle = {
    border: "1px solid #0963EB",
  };

  const navigate = useNavigate();
  
  // Extract contact person data from the nested structure
  const contactPerson = useMemo(() => contactPersonData?.data?.contactPerson || {}, [contactPersonData]);

  const [formData, setFormData] = useState({
    address: profileData?.address || "",
    country: profileData?.country || "",
    state: profileData?.state || "",
    city: profileData?.city || "",
    companyName: profileData?.companyName || "",
    website: profileData?.website || "",
    phoneNumber: profileData?.phoneNumber || "",
    email: profileData?.email || "",
  });

  const [contactFormData, setContactFormData] = useState({
    contactName: contactPerson?.contactName || "",
    emailAddress: contactPerson?.emailAddress || "",
    phoneNumber: contactPerson?.phoneNumber || "",
    country: contactPerson?.country || "",
    state: contactPerson?.state || "",
    city: contactPerson?.city || "",
  });

  const [editOverlays, setEditOverlays] = useState({
    address: false,
    country: false,
    state: false,
    city: false,
    companyName: false,
    website: false,
    phoneNumber: false,
    registrationNumber: false,
    email: false,
    contactName: false,
    contactEmail: false,
    contactPhone: false,
    contactCountry: false,
    contactState: false,
    contactCity: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value.trim() !== "" ? value : null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updatedFields = {};

    for (const key in formData) {
      if (formData[key] !== profileData[key]) {
        updatedFields[key] = formData[key];
      }
    }

    try {
      mutation.mutateAsync(updatedFields, {
        onSuccess: () => {
          queryClient.invalidateQueries("profile");
          toast.success("Profile updated successfully!");
          setEditOverlays((prev) => {
            const newOverlays = { ...prev };
            for (const key in newOverlays) {
              if (newOverlays[key]) {
                newOverlays[key] = false;
                break;
              }
            }
            return newOverlays;
          });
        },
        onError: (error) => {
          toast.error(error.message || "Failed to update profile");
        }
      });
    } catch (error) {
      toast.error(error.message || "Failed to update profile");
    }
  };

  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactFormData((prevData) => ({
      ...prevData,
      [name]: value.trim() !== "" ? value : null,
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const updatedFields = {};

    for (const key in contactFormData) {
      if (contactFormData[key] !== contactPerson[key]) {
        updatedFields[key] = contactFormData[key];
      }
    }

    try {
      updateContactPerson(updatedFields, {
        onSuccess: () => {
          queryClient.invalidateQueries("contactPerson");
          toast.success("Contact person updated successfully!");
          setEditOverlays((prev) => {
            const newOverlays = { ...prev };
            for (const key in newOverlays) {
              if (newOverlays[key]) {
                newOverlays[key] = false;
                break;
              }
            }
            return newOverlays;
          });
        },
        onError: (error) => {
          toast.error(error.message || "Failed to update contact person");
        }
      });
    } catch (error) {
      toast.error(error.message || "Failed to update contact person");
    }
  };

  useEffect(() => {
    setFormData({
      address: profileData?.address || "",
      country: profileData?.country || "",
      state: profileData?.state || "",
      city: profileData?.city || "",
      companyName: profileData?.companyName || "",
      website: profileData?.website || "",
      phoneNumber: profileData?.phoneNumber || "",
      email: profileData?.email || "",
    });
  }, [profileData]);

  useEffect(() => {
    setContactFormData({
      contactName: contactPerson?.contactName || "",
      emailAddress: contactPerson?.emailAddress || "",
      phoneNumber: contactPerson?.phoneNumber || "",
      country: contactPerson?.country || "",
      state: contactPerson?.state || "",
      city: contactPerson?.city || "",
    });
  }, [contactPerson]);

  const toggleEditOverlay = (fieldName) => {
    setEditOverlays((prevEditOverlays) => {
      const newOverlays = {
        ...prevEditOverlays,
        [fieldName]: !prevEditOverlays[fieldName],
      };
      
      if (!newOverlays[fieldName]) {
        const overlay = document.querySelector(`.overlay-container[data-field="${fieldName}"]`);
        if (overlay) {
          overlay.classList.remove('active');
        }
      }
      
      return newOverlays;
    });
  };

  return (
    <div className="update-customer">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="wrap">
        <div className="switch">
          <div
            className="per-info"
            onClick={() => {
              setActiveSection("UpdatecustomerProfile");
              navigate("/charterer-dashboard/updatecustomerprofile");
            }}
            style={activeSection === "UpdatecustomerProfile" ? activeStyle : {}}
          >
            <h3>Personal Info</h3>
            <p>Manage personal details</p>
          </div>

          <div
            className="document"
            onClick={() => {
              setActiveSection("document");
              navigate("/charterer-dashboard/typeofupdate");
            }}
            style={activeSection === "document" ? activeStyle : {}}
          >
            <h3>Documents</h3>
            <p>Update License and other documents</p>
          </div>

          <div
            className="change-pass"
            onClick={() => {
              setActiveSection("changePass");
              navigate("/charterer-dashboard/updatecustomerpass");
            }}
            style={activeSection === "changePass" ? activeStyle : {}}
          >
            <h3>Password</h3>
            <p>Change and manage password</p>
          </div>
        </div>
        
        {/* Mobile Navigation Tabs */}
        <div className="mobile-tabs">
          <div
            className={`mobile-tab ${activeSection === "UpdatecustomerProfile" ? "active" : ""}`}
            onClick={() => {
              setActiveSection("UpdatecustomerProfile");
              navigate("/charterer-dashboard/updatecustomerprofile");
            }}
          >
            <h3>Personal Info</h3>
            <p>Manage personal details</p>
          </div>
          <div
            className={`mobile-tab ${activeSection === "document" ? "active" : ""}`}
            onClick={() => {
              setActiveSection("document");
              navigate("/charterer-dashboard/typeofupdate");
            }}
          >
            <h3>Documents</h3>
            <p>Update License and other documents</p>
          </div>
          <div
            className={`mobile-tab ${activeSection === "changePass" ? "active" : ""}`}
            onClick={() => {
              setActiveSection("changePass");
              navigate("/charterer-dashboard/updatecustomerpass");
            }}
          >
            <h3>Password</h3>
            <p>Change and manage password</p>
          </div>
        </div>
        
        <div className="card">
          <h2>Personal Profile</h2>
          <form onSubmit={handleSubmit}>
            <div className="data-card">
              <label>
                <div className="data">
                  <h3>Website</h3>
                  {profileLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {" "}
                      {profileData?.website ? (
                        <a 
                          href={profileData.website.startsWith('http') ? profileData.website : `https://${profileData.website}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {profileData.website}
                        </a>
                      ) : (
                        "Not provided"
                      )}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("website")}>Edit</span>
                {editOverlays.website && (
                  <div className="overlay-container" data-field="website">
                    <div className="card">
                      <div className="header">
                        <p>Edit Website</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("website")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Website</p>
                        <input
                          type="text"
                          name="website"
                          value={
                            formData.website !== null ? formData.website : ""
                          }
                          onChange={handleInputChange}
                        />
                      </div>
                      <button type="submit" disabled={mutation.isLoading}>
                        {mutation.isLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Phone Number</h3>
                  {profileLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {" "}
                      {profileData?.phoneNumber
                        ? profileData?.phoneNumber
                        : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("phoneNumber")}>
                  Edit
                </span>
                {editOverlays.phoneNumber && (
                  <div className="overlay-container" data-field="phoneNumber">
                    <div className="card">
                      <div className="header">
                        <p>Edit Phone Number</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("phoneNumber")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Phone Number</p>
                        <input
                          type="text"
                          name="phoneNumber"
                          value={
                            formData.phoneNumber !== null
                              ? formData.phoneNumber
                              : ""
                          }
                          onChange={handleInputChange}
                        />
                      </div>
                      <button type="submit" disabled={mutation.isLoading}>
                        {mutation.isLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Country</h3>
                  {profileLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {" "}
                      {profileData?.country
                        ? profileData?.country
                        : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("country")}>
                  Edit
                </span>
                {editOverlays.country && (
                  <div className="overlay-container" data-field="country">
                    <div className="card">
                      <div className="header">
                        <p>Edit Country</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("country")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Country</p>
                        <input
                          type="text"
                          name="country"
                          value={
                            formData.country !== null
                              ? formData.country
                              : ""
                          }
                          onChange={handleInputChange}
                        />
                      </div>
                      <button type="submit" disabled={mutation.isLoading}>
                        {mutation.isLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>State</h3>
                  {profileLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {" "}
                      {profileData?.state ? profileData?.state : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("state")}>Edit</span>
                {editOverlays.state && (
                  <div className="overlay-container" data-field="state">
                    <div className="card">
                      <div className="header">
                        <p>Edit State</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("state")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>State</p>
                        <input
                          type="text"
                          name="state"
                          value={formData.state !== null ? formData.state : ""}
                          onChange={handleInputChange}
                        />
                      </div>
                      <button type="submit" disabled={mutation.isLoading}>
                        {mutation.isLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>City</h3>
                  {profileLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {" "}
                      {profileData?.city ? profileData?.city : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("city")}>Edit</span>
                {editOverlays.city && (
                  <div className="overlay-container" data-field="city">
                    <div className="card">
                      <div className="header">
                        <p>Edit City</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("city")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>City</p>
                        <input
                          type="text"
                          name="city"
                          value={formData.city !== null ? formData.city : ""}
                          onChange={handleInputChange}
                        />
                      </div>
                      <button type="submit" disabled={mutation.isLoading}>
                        {mutation.isLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Address</h3>
                  {profileLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {" "}
                      {profileData?.address
                        ? profileData?.address
                        : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("address")}>Edit</span>
                {editOverlays.address && (
                  <div className="overlay-container" data-field="address">
                    <div className="card">
                      <div className="header">
                        <p>Edit Address</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("address")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Address</p>
                        <input
                          type="text"
                          name="address"
                          value={
                            formData.address !== null ? formData.address : ""
                          }
                          onChange={handleInputChange}
                        />
                      </div>
                      <button type="submit" disabled={mutation.isLoading}>
                        {mutation.isLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Company Name</h3>
                  {profileLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {" "}
                      {profileData?.companyName
                        ? profileData?.companyName
                        : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("companyName")}>
                  Edit
                </span>
                {editOverlays.companyName && (
                  <div className="overlay-container" data-field="companyName">
                    <div className="card">
                      <div className="header">
                        <p>Edit Company Name</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("companyName")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Company Name</p>
                        <input
                          type="text"
                          name="companyName"
                          value={
                            formData.companyName !== null
                              ? formData.companyName
                              : ""
                          }
                          onChange={handleInputChange}
                        />
                      </div>
                      <button type="submit" disabled={mutation.isLoading}>
                        {mutation.isLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Email</h3>
                  {profileLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {" "}
                      {profileData?.email ? profileData?.email : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("email")}>Edit</span>
                {editOverlays.email && (
                  <div className="overlay-container" data-field="email">
                    <div className="card">
                      <div className="header">
                        <p>Edit Email</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("email")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Email</p>
                        <input
                          type="email"
                          name="email"
                          value={formData.email !== null ? formData.email : ""}
                          onChange={handleInputChange}
                        />
                      </div>
                      <button type="submit" disabled={mutation.isLoading}>
                        {mutation.isLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>
            </div>
          </form>

          {/* Only render contact person section if data is available */}
          {contactPersonData?.data?.contactPerson && (
            <form onSubmit={handleContactSubmit}>
              <h2>Contact Person</h2>
              <div className="data-card">
                <label>
                  <div className="data">
                    <h3>Contact Name</h3>
                    {contactLoading ? (
                      <Skeleton count={1} height={10} width={50} />
                    ) : (
                      <p>
                        {contactPerson?.contactName
                          ? contactPerson?.contactName
                          : "Not provided"}
                      </p>
                    )}
                  </div>
                  <span onClick={() => toggleEditOverlay("contactName")}>Edit</span>
                  {editOverlays.contactName && (
                    <div className="overlay-container" data-field="contactName">
                      <div className="card">
                        <div className="header">
                          <p>Edit Contact Name</p>
                          <HiOutlineXMark
                            onClick={() => toggleEditOverlay("contactName")}
                            className="icon"
                          />
                        </div>
                        <div className="in">
                          <p>Contact Name</p>
                          <input
                            type="text"
                            name="contactName"
                            value={contactFormData.contactName || ""}
                            onChange={handleContactInputChange}
                          />
                        </div>
                        <button type="submit" disabled={updatingContact}>
                          {updatingContact ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  )}
                </label>

                <label>
                  <div className="data">
                    <h3>Contact Email</h3>
                    {contactLoading ? (
                      <Skeleton count={1} height={10} width={50} />
                    ) : (
                      <p>
                        {contactPerson?.emailAddress
                          ? contactPerson?.emailAddress
                          : "Not provided"}
                      </p>
                    )}
                  </div>
                  <span onClick={() => toggleEditOverlay("contactEmail")}>Edit</span>
                  {editOverlays.contactEmail && (
                    <div className="overlay-container" data-field="contactEmail">
                      <div className="card">
                        <div className="header">
                          <p>Edit Contact Email</p>
                          <HiOutlineXMark
                            onClick={() => toggleEditOverlay("contactEmail")}
                            className="icon"
                          />
                        </div>
                        <div className="in">
                          <p>Contact Email</p>
                          <input
                            type="email"
                            name="emailAddress"
                            value={contactFormData.emailAddress || ""}
                            onChange={handleContactInputChange}
                          />
                        </div>
                        <button type="submit" disabled={updatingContact}>
                          {updatingContact ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  )}
                </label>

                <label>
                  <div className="data">
                    <h3>Contact Phone Number</h3>
                    {contactLoading ? (
                      <Skeleton count={1} height={10} width={50} />
                    ) : (
                      <p>
                        {contactPerson?.phoneNumber
                          ? contactPerson?.phoneNumber
                          : "Not provided"}
                      </p>
                    )}
                  </div>
                  <span onClick={() => toggleEditOverlay("contactPhone")}>Edit</span>
                  {editOverlays.contactPhone && (
                    <div className="overlay-container" data-field="contactPhone">
                      <div className="card">
                        <div className="header">
                          <p>Edit Contact Phone Number</p>
                          <HiOutlineXMark
                            onClick={() => toggleEditOverlay("contactPhone")}
                            className="icon"
                          />
                        </div>
                        <div className="in">
                          <p>Contact Phone Number</p>
                          <input
                            type="text"
                            name="phoneNumber"
                            value={contactFormData.phoneNumber || ""}
                            onChange={handleContactInputChange}
                          />
                        </div>
                        <button type="submit" disabled={updatingContact}>
                          {updatingContact ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  )}
                </label>

                <label>
                  <div className="data">
                    <h3>Contact Country</h3>
                    {contactLoading ? (
                      <Skeleton count={1} height={10} width={50} />
                    ) : (
                      <p>
                        {contactPerson?.country
                          ? contactPerson?.country
                          : "Not provided"}
                      </p>
                    )}
                  </div>
                  <span onClick={() => toggleEditOverlay("contactCountry")}>Edit</span>
                  {editOverlays.contactCountry && (
                    <div className="overlay-container" data-field="contactCountry">
                      <div className="card">
                        <div className="header">
                          <p>Edit Contact Country</p>
                          <HiOutlineXMark
                            onClick={() => toggleEditOverlay("contactCountry")}
                            className="icon"
                          />
                        </div>
                        <div className="in">
                          <p>Contact Country</p>
                          <input
                            type="text"
                            name="country"
                            value={contactFormData.country || ""}
                            onChange={handleContactInputChange}
                          />
                        </div>
                        <button type="submit" disabled={updatingContact}>
                          {updatingContact ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  )}
                </label>

                <label>
                  <div className="data">
                    <h3>Contact State</h3>
                    {contactLoading ? (
                      <Skeleton count={1} height={10} width={50} />
                    ) : (
                      <p>
                        {contactPerson?.state ? contactPerson?.state : "Not provided"}
                      </p>
                    )}
                  </div>
                  <span onClick={() => toggleEditOverlay("contactState")}>Edit</span>
                  {editOverlays.contactState && (
                    <div className="overlay-container" data-field="contactState">
                      <div className="card">
                        <div className="header">
                          <p>Edit Contact State</p>
                          <HiOutlineXMark
                            onClick={() => toggleEditOverlay("contactState")}
                            className="icon"
                          />
                        </div>
                        <div className="in">
                          <p>Contact State</p>
                          <input
                            type="text"
                            name="state"
                            value={contactFormData.state || ""}
                            onChange={handleContactInputChange}
                          />
                        </div>
                        <button type="submit" disabled={updatingContact}>
                          {updatingContact ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  )}
                </label>

                <label>
                  <div className="data">
                    <h3>Contact City</h3>
                    {contactLoading ? (
                      <Skeleton count={1} height={10} width={50} />
                    ) : (
                      <p>
                        {contactPerson?.city ? contactPerson?.city : "Not provided"}
                      </p>
                    )}
                  </div>
                  <span onClick={() => toggleEditOverlay("contactCity")}>Edit</span>
                  {editOverlays.contactCity && (
                    <div className="overlay-container" data-field="contactCity">
                      <div className="card">
                        <div className="header">
                          <p>Edit Contact City</p>
                          <HiOutlineXMark
                            onClick={() => toggleEditOverlay("contactCity")}
                            className="icon"
                          />
                        </div>
                        <div className="in">
                          <p>Contact City</p>
                          <input
                            type="text"
                            name="city"
                            value={contactFormData.city || ""}
                            onChange={handleContactInputChange}
                          />
                        </div>
                        <button type="submit" disabled={updatingContact}>
                          {updatingContact ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  )}
                </label>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdatecustomerProfile;
